import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, Hotel, UtensilsCrossed, School, BedDouble, Briefcase, Home as HomeIcon, Shapes } from "lucide-react";
import plannerApi, { FREQUENCIES, track } from "@/lib/plannerApi";
import { PlannerShell } from "@/components/planner/PlannerShell";

const EST_ICONS = { HOTEL: Hotel, RESTAURANT: UtensilsCrossed, SCHOOL: School, BOARDING: BedDouble,
                    OFFICE: Briefcase, GUEST_HOUSE: HomeIcon, OTHER: Shapes };
const SIZE_LABEL = { HOTEL: "Rooms", GUEST_HOUSE: "Rooms", RESTAURANT: "Covers / seats", SCHOOL: "Students",
                     BOARDING: "Residents", OFFICE: "Employees", OTHER: "People served" };
const DIETS = ["Vegetarian", "Non-vegetarian", "Eggetarian", "Jain"];

const Field = ({ label, children }) => (
    <label className="block"><span className="hdb-label">{label}</span>{children}</label>
);

export default function PlannerProfile() {
    const { type: rawType } = useParams();
    const type = (rawType || "home").toUpperCase();
    const isBiz = type === "BUSINESS";
    const nav = useNavigate();
    const [ests, setEsts] = useState([]);
    const [p, setP] = useState({ establishment_type: "", language: "en", frequency: "monthly", city: "", pin: "",
                                 household: {}, organization: {}, departments: [] });
    const [dept, setDept] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        track("planner_started", type);
        plannerApi.get("/templates", { params: { type: "BUSINESS" } }).then((r) => setEsts(r.data.establishment_types));
        plannerApi.get(`/profile/${type}`).then((r) => r.data?.id && setP((x) => ({ ...x, ...r.data }))).catch(() => {});
    }, [type]);

    const hh = (k, v) => setP({ ...p, household: { ...p.household, [k]: v } });
    const org = (k, v) => setP({ ...p, organization: { ...p.organization, [k]: v } });

    const go = async () => {
        if (isBiz && !p.establishment_type) return toast.error("Pick your establishment type");
        setBusy(true);
        try {
            await plannerApi.put(`/profile/${type}`, p);
            const lists = (await plannerApi.get("/lists")).data.filter((l) => l.planner_type === type && l.list_type === "MASTER");
            const lst = lists[0] || (await plannerApi.post("/lists", { planner_type: type, list_type: "MASTER",
                name: isBiz ? `${p.organization.name || "Business"} — master list` : "My monthly home list" })).data;
            nav(`/portal/essentials-planner/list/${lst.id}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); setBusy(false); }
    };

    return (
        <PlannerShell>
            <div data-testid="planner-profile" className="max-w-3xl">
                <div className="text-xs font-black uppercase tracking-widest text-emerald-700">{isBiz ? "Business planner" : "Home planner"} · step 1 of 2</div>
                <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mt-2" style={{ fontFamily: "'Chivo',sans-serif" }}>
                    {isBiz ? "Tell us about your establishment" : "A little about your home"}
                </h1>
                <p className="text-slate-500 mt-2 text-sm">Everything here is optional{isBiz ? " except the type" : ""} — it only helps us suggest the right checklist.</p>

                {isBiz && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-6" data-testid="planner-est-types">
                        {ests.map((e) => {
                            const Icon = EST_ICONS[e.key] || Shapes;
                            const on = p.establishment_type === e.key;
                            return (
                                <button key={e.key} onClick={() => setP({ ...p, establishment_type: e.key })}
                                        data-testid={`planner-est-${e.key.toLowerCase()}`}
                                        className={`rounded-2xl border-2 p-4 text-left transition-colors ${on ? "border-emerald-600 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                                    <Icon size={20} className={on ? "text-emerald-700" : "text-slate-500"}/>
                                    <div className="font-bold text-sm text-slate-900 mt-2">{e.label}</div>
                                </button>
                            );
                        })}
                    </div>
                )}

                <div className="rounded-2xl bg-white border border-slate-200 p-5 mt-6 grid sm:grid-cols-2 gap-4">
                    {isBiz ? (
                        <>
                            <Field label="Institution name"><input className="hdb-input" value={p.organization.name || ""} onChange={(e) => org("name", e.target.value)} data-testid="planner-org-name"/></Field>
                            <Field label="Contact person"><input className="hdb-input" value={p.organization.contact_person || ""} onChange={(e) => org("contact_person", e.target.value)} data-testid="planner-org-contact"/></Field>
                            <Field label={SIZE_LABEL[p.establishment_type] || "Size"}><input type="number" min="0" className="hdb-input" value={p.organization.size || ""} onChange={(e) => org("size", e.target.value)} data-testid="planner-org-size"/></Field>
                            <Field label="GSTIN (optional)"><input className="hdb-input uppercase" value={p.organization.gstin || ""} onChange={(e) => org("gstin", e.target.value.toUpperCase())} data-testid="planner-org-gstin"/></Field>
                            <Field label="Preferred brands"><input className="hdb-input" value={p.organization.preferred_brands || ""} onChange={(e) => org("preferred_brands", e.target.value)} placeholder="e.g. Fortune, Aashirvaad" data-testid="planner-org-brands"/></Field>
                            <Field label="Departments">
                                <div className="flex gap-2">
                                    <input className="hdb-input" value={dept} onChange={(e) => setDept(e.target.value)} placeholder="Kitchen, Housekeeping…"
                                           onKeyDown={(e) => { if (e.key === "Enter" && dept.trim()) { setP({ ...p, departments: [...p.departments, dept.trim()] }); setDept(""); } }}
                                           data-testid="planner-dept-input"/>
                                    <button className="hdb-btn hdb-btn-ghost" data-testid="planner-dept-add"
                                            onClick={() => { if (dept.trim()) { setP({ ...p, departments: [...p.departments, dept.trim()] }); setDept(""); } }}>Add</button>
                                </div>
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {p.departments.map((d, i) => (
                                        <button key={d + i} onClick={() => setP({ ...p, departments: p.departments.filter((_, j) => j !== i) })}
                                                className="px-2 py-0.5 rounded-full bg-slate-100 text-xs font-bold text-slate-700" data-testid={`planner-dept-${i}`}>{d} ×</button>
                                    ))}
                                </div>
                            </Field>
                        </>
                    ) : (
                        <>
                            <Field label="Adults"><input type="number" min="0" className="hdb-input" value={p.household.adults ?? ""} onChange={(e) => hh("adults", e.target.value)} data-testid="planner-hh-adults"/></Field>
                            <Field label="Children"><input type="number" min="0" className="hdb-input" value={p.household.children ?? ""} onChange={(e) => hh("children", e.target.value)} data-testid="planner-hh-children"/></Field>
                            <Field label="Food preference">
                                <select className="hdb-input" value={p.household.diet || ""} onChange={(e) => hh("diet", e.target.value)} data-testid="planner-hh-diet">
                                    <option value="">Prefer not to say</option>
                                    {DIETS.map((d) => <option key={d}>{d}</option>)}
                                </select>
                            </Field>
                            <div className="flex items-end gap-4 text-sm">
                                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!p.household.pets} onChange={(e) => hh("pets", e.target.checked)} data-testid="planner-hh-pets"/> Pets</label>
                                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!p.household.elderly} onChange={(e) => hh("elderly", e.target.checked)} data-testid="planner-hh-elderly"/> Elderly care</label>
                            </div>
                        </>
                    )}
                    <Field label="City / PIN">
                        <div className="flex gap-2">
                            <input className="hdb-input" value={p.city} onChange={(e) => setP({ ...p, city: e.target.value })} placeholder="City" data-testid="planner-city"/>
                            <input className="hdb-input !w-28" value={p.pin} inputMode="numeric" maxLength={6} onChange={(e) => setP({ ...p, pin: e.target.value })} placeholder="PIN" data-testid="planner-pin"/>
                        </div>
                    </Field>
                    <Field label="How often do you buy?">
                        <select className="hdb-input" value={p.frequency} onChange={(e) => setP({ ...p, frequency: e.target.value })} data-testid="planner-frequency">
                            {FREQUENCIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                    </Field>
                    <Field label="Language">
                        <select className="hdb-input" value={p.language} onChange={(e) => setP({ ...p, language: e.target.value })} data-testid="planner-language">
                            <option value="en">English</option><option value="hi">हिन्दी (Hindi)</option>
                        </select>
                    </Field>
                </div>
                <button onClick={go} disabled={busy} data-testid="planner-profile-continue"
                        className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-emerald-600 text-white font-black hover:bg-emerald-500 shadow-lg">
                    {busy ? <Loader2 size={16} className="animate-spin"/> : null} Continue to my list <ArrowRight size={16}/>
                </button>
            </div>
        </PlannerShell>
    );
}
